import { useState } from 'react';
import { PageHeader } from '@/components/brand/PageHeader';
import { Panel } from '@/components/brand/Panel';
import { useApiQuery } from '@/lib/hooks';
import { EmptyState, ErrorState, LoadingState, UnauthorizedState } from '@/components/brand/States';
import { useAuth } from '@/lib/auth';
import { SevPill } from '@/components/brand/SevPill';
import { explainAuditEvent } from '@mb-it-ops/audit-classify';

interface AuditEvent {
  id: string;
  event_id: string;
  occurred_at: number;
  source: string;
  event_type: string;
  status: string;
  severity: string | null;
  actor: string | null;
  category: string | null;
  target: string | null;
  metadata?: Record<string, unknown>;
}

interface SearchParams {
  from: number;
  to: number;
  source: string;
  category: string;
  severity: string;
  q: string;
}

function toUnixMs(yyyymmdd: string, endOfDay = false): number {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  if (!y || !m || !d) return 0;
  const date = new Date(
    Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0),
  );
  return date.getTime();
}

function defaultIso(daysAgo: number): string {
  return new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** Display timestamps in Europe/London per build guide §3 (matches WebApps). */
function fmtLondon(ms: number): string {
  return new Date(ms).toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function sevLevel(severity: string | null): 'crit' | 'warn' | 'info' | 'ok' {
  if (severity === 'critical') return 'crit';
  if (severity === 'warning') return 'warn';
  if (severity === 'info') return 'info';
  return 'ok';
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="eyebrow text-fg-muted">{label}</dt>
      <dd className="mono mono-sm break-all">{value}</dd>
    </div>
  );
}

export function AuditLogPage() {
  const { me, isUnauthorized } = useAuth();
  const [from, setFrom] = useState(defaultIso(7));
  const [to, setTo] = useState(defaultIso(0));
  const [source, setSource] = useState('');
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('');
  const [q, setQ] = useState('');
  const [params, setParams] = useState<SearchParams | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Keep useApiQuery above the unauthorized / non-admin bail-outs so the hook
  // count is stable across renders (see Archive.tsx note). params starts null
  // so the query is disabled until the first search.
  const search = useApiQuery<{ items: AuditEvent[]; next_cursor?: string }>({
    enabled: params !== null,
    queryKey: ['audit', params],
    queryFn: async () => {
      const u = new URL('/api/audit/events', location.origin);
      if (params) {
        u.searchParams.set('from', String(params.from));
        u.searchParams.set('to', String(params.to));
        if (params.source) u.searchParams.set('source', params.source);
        if (params.category) u.searchParams.set('category', params.category);
        if (params.severity) u.searchParams.set('severity', params.severity);
        if (params.q) u.searchParams.set('q', params.q);
        u.searchParams.set('limit', '200');
      }
      const res = await fetch(u.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      return res.json();
    },
  });

  if (isUnauthorized) return <UnauthorizedState />;
  if (me && me.role !== 'admin') {
    return (
      <div>
        <PageHeader eyebrow="Governance" title="Audit Log" />
        <Panel title="Admin changes & sensitive reads">
          <EmptyState
            title="Admin-only"
            hint="Records administrative changes and flagged sensitive-data reads across M365 and Egnyte."
          />
        </Panel>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Governance"
        title="Audit Log"
        meta="M365 + Egnyte · admin/app changes & sensitive reads"
      />
      <Panel title="Filter">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setParams({ from: toUnixMs(from), to: toUnixMs(to, true), source, category, severity, q });
          }}
          className="grid grid-cols-1 md:grid-cols-3 gap-3 text-small"
        >
          <label className="flex flex-col gap-1">
            <span className="eyebrow">From (UTC)</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-rule px-3 py-2 bg-bg-page mono" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="eyebrow">To (UTC)</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-rule px-3 py-2 bg-bg-page mono" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="eyebrow">Source</span>
            <select value={source} onChange={(e) => setSource(e.target.value)} className="border border-rule px-3 py-2 bg-bg-page">
              <option value="">Any</option>
              <option value="m365_audit">m365_audit</option>
              <option value="egnyte_audit">egnyte_audit</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="eyebrow">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="border border-rule px-3 py-2 bg-bg-page">
              <option value="">Any</option>
              <option value="change">change</option>
              <option value="sensitive_read">sensitive_read</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="eyebrow">Severity</span>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="border border-rule px-3 py-2 bg-bg-page">
              <option value="">Any</option>
              <option value="info">info</option>
              <option value="warning">warning</option>
              <option value="critical">critical</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="eyebrow">Contains text</span>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="operation, actor, path…" className="border border-rule px-3 py-2 bg-bg-page" />
          </label>
          <div className="md:col-span-3 flex justify-end">
            <button type="submit" disabled={search.isFetching} className="bg-fg-default text-mb-spring-wood px-4 py-2 text-eyebrow disabled:opacity-40">
              {search.isFetching ? 'Loading…' : 'Search audit log'}
            </button>
          </div>
        </form>
      </Panel>

      {params && (
        <Panel
          title="Results"
          subtitle={
            search.data
              ? `${search.data.items.length} event(s)${search.data.next_cursor ? ' · showing first 200' : ''}`
              : undefined
          }
        >
          {search.isLoading && <LoadingState label="Loading audit events…" />}
          {search.error && <ErrorState title="Search failed" detail={String((search.error as Error).message)} />}
          {search.data && search.data.items.length === 0 && (
            <EmptyState
              title="No matching audit events"
              hint="The M365 poller runs every 10 min and Egnyte every 15 min. Only admin/app changes and flagged sensitive reads are kept — routine reads are filtered out."
            />
          )}
          {search.data && search.data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-small table-fixed">
                <colgroup>
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
                <thead className="text-fg-muted text-eyebrow">
                  <tr>
                    <th className="text-left py-2">When (London)</th>
                    <th className="text-left">Source</th>
                    <th className="text-left">Actor</th>
                    <th className="text-left">Operation</th>
                    <th className="text-left">Category</th>
                    <th className="text-left">Severity</th>
                    <th className="text-left">Target</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rule">
                  {search.data.items.flatMap((e) => {
                    const open = expandedId === e.id;
                    const rows = [
                      <tr
                        key={e.id}
                        onClick={() => setExpandedId(open ? null : e.id)}
                        className="cursor-pointer hover:bg-bg-subtle"
                      >
                        <td className="py-2 mono mono-sm whitespace-nowrap">{fmtLondon(e.occurred_at)}</td>
                        <td className="mono truncate">{e.source}</td>
                        <td className="truncate" title={e.actor ?? ''}>{e.actor ?? '—'}</td>
                        <td className="mono truncate" title={e.event_type}>{e.event_type}</td>
                        <td className="text-caption">{e.category ?? '—'}</td>
                        <td>{e.severity ? <SevPill level={sevLevel(e.severity)} label={e.severity} /> : '—'}</td>
                        <td className="truncate text-fg-muted" title={e.target ?? ''}>{e.target ?? '—'}</td>
                      </tr>,
                    ];
                    if (open) {
                      rows.push(
                        <tr key={`${e.id}-detail`} className="bg-bg-subtle">
                          <td colSpan={7} className="py-3 px-3 align-top">
                            <p className="text-small mb-3">
                              {explainAuditEvent({
                                source: e.source,
                                event_type: e.event_type,
                                actor: e.actor,
                                target: e.target,
                                category: e.category,
                                metadata: e.metadata ?? null,
                              })}
                            </p>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-caption">
                              <DetailItem label="When (UTC)" value={new Date(e.occurred_at).toISOString()} />
                              <DetailItem label="Status" value={e.status} />
                              <DetailItem label="Event ID" value={e.event_id} />
                              {Object.entries(e.metadata ?? {}).map(([k, v]) =>
                                v == null || v === '' ? null : (
                                  <DetailItem key={k} label={k} value={String(v)} />
                                ),
                              )}
                            </dl>
                          </td>
                        </tr>,
                      );
                    }
                    return rows;
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
